import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { ReactComponent as NoJobIcon } from '../../assets/images/no-job-icon.svg';
import { RecommendationItem } from '../../components/Dashboard/RecommendationItem/RecommendationItem';
import Header from '../../components/shared/Header/Header';
import { Pagination } from '../../components/shared/Pagination/Pagination';
import SeeMoreButton from '../../components/shared/SeeMoreButton/SeeMoreButton';
import ActivityFeedLoader from '../../components/shared/SkeletonLoaders/ActivityFeedLoader/ActivityFeedLoader';
import { IsMobile } from '../../helpers/IsMobile';
import { getReferralRecommendations } from '../../redux/actions/dashboardActions';
import {
  selectReferralRecommendations,
  selectReferralRecommendationsLoading,
  selectReferralRecommendationsTotal,
} from '../../selectors/dashboardSelectors';
import { selectCurrentOrganization } from '../../selectors/organizationSelectors';

export const RecommendationsPage = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const aiRecommendationsEnabled = currentOrganization?.ai_recommendations_for_members !== false;
  const LIMIT = 3;
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(LIMIT);

  const referralRecommendations = useSelector(selectReferralRecommendations);
  const referralRecommendationsTotal = useSelector(selectReferralRecommendationsTotal) || 0;
  const referralRecommendationsLoading = useSelector(selectReferralRecommendationsLoading);
  const loading = useSelector((state) => state.globalReducer.loading);
  const isMobile = IsMobile();

  const totalPages = Math.ceil(referralRecommendationsTotal / LIMIT);

  const fetchRecommendations = (page) => {
    const offset = (page - 1) * LIMIT;
    dispatch(getReferralRecommendations({ limit: LIMIT, offset }));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRecommendations(newPage);
  };

  const handleSeeMore = () => {
    const newDisplayCount = displayCount + LIMIT;
    setDisplayCount(newDisplayCount);
    fetchRecommendations(Math.ceil(newDisplayCount / LIMIT));
  };

  useEffect(() => {
    if (!aiRecommendationsEnabled) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchRecommendations(1);
  }, [aiRecommendationsEnabled]);

  const renderRecommendationsLoader = () => {
    if ((referralRecommendationsLoading && referralRecommendationsTotal === 0) || loading) {
      return <ActivityFeedLoader itemCount={6} fromRecommendationsPage />;
    }
  };

  const renderRecommendationsEmptyState = () => {
    if (!referralRecommendationsLoading && referralRecommendationsTotal === 0 && !loading) {
      return (
        <div className="flex flex-col items-center justify-center p-4 text-center md:p-8">
          <NoJobIcon />
          <p className="text-[#0E1829] font-medium text-base">{t('recommendations.noMatchingJobs')}</p>
        </div>
      );
    }
  };

  return (
    <>
      <div className="max-md:hidden">
        <Header title={t('recommendations.referralForYou')} backButton={t('headers.backToDashboard')} />
      </div>
      <div className="h-[calc(100vh-84px)] overflow-y-scroll">
        <div className="flex items-center justify-between h-16 px-3 py-4 bg-white md:hidden">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 p-0 mt-1 text-xs font-medium text-gray-500 bg-transparent border-none"
          >
            <ArrowLeft />
          </button>
          <p className="text-xl font-semibold text-gray-900">{t('recommendations.referralForYou')}</p>
          <span />
        </div>
        <div className="flex items-center justify-center p-[26px] max-w-screen-lg mx-auto">
          <div className="flex flex-col w-full gap-3 p-3 bg-white rounded-xl shadow-card lg:w-3/4">
            {renderRecommendationsLoader()}
            {!referralRecommendationsLoading && referralRecommendationsTotal > 0 ? (
              <>
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-lg font-semibold text-gray-900">{t('recommendations.referralForYou')}</p>
                    <p className="text-xs text-gray-600">{t('navigation.recommendations')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {referralRecommendations
                    .filter((recommendation) => recommendation.job_data)
                    .slice(0, displayCount)
                    .map((recommendation, index) => (
                      <RecommendationItem
                        key={recommendation.id || `recommendation-${index}`}
                        recommendation={recommendation}
                      />
                    ))}
                </div>
                <div className="flex justify-center max-md:hidden">
                  <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={handlePageChange} />
                </div>
                {isMobile && displayCount < referralRecommendationsTotal && (
                  <div className="flex justify-center">
                    <SeeMoreButton label={t('buttons.seeMore')} onClick={handleSeeMore} />
                  </div>
                )}
              </>
            ) : (
              renderRecommendationsEmptyState()
            )}
          </div>
        </div>
      </div>
    </>
  );
};
